/**
 * Removes a paired gift/subscription cart line (added by product-gift-pairing.js)
 * once its host variant is no longer in the cart. Loaded globally
 * (theme-head-scripts.liquid) since the Rebuy cart drawer opens from any page,
 * not just the PDP that added the gift.
 *
 * Pairs are matched by variant id and held in localStorage, because Rebuy
 * rebuilds the subscription line — new key, empty properties — so anything held
 * on the cart itself does not survive. Line properties are still read as a
 * fallback for a line Rebuy has not touched yet.
 *
 * Pairs are learned from the /cart/add.js payload, so this covers bundles added
 * anywhere: hp-vrh on the homepage adds host and gift as one multi-item call,
 * the PDP adds them as two calls via product-gift-pairing.js.
 */
(function () {
  if (window.__vrGiftSyncBound) return;
  window.__vrGiftSyncBound = true;

  var GIFT_PROPERTY = '_gift_for_variant_id';
  var STORAGE_KEY = 'vr_gift_pairs';

  // Buying a paid upgrade from the Rebuy cart upsell replaces the free gift, so
  // the shopper is not left paying for the annual plan next to a free monthly
  // one. Gift variant id -> variant ids that supersede it. The upsell products
  // live in Rebuy, not in the theme, so these ids have to be listed here:
  //   51307761467705 Stress Protection Plan (free, monthly)
  //   52015124250937 Stress Protection Plan Annual (paid)
  var SUPERSEDED_BY = {
    '51307761467705': ['52015124250937'],
  };

  var syncing = false;
  var dirty = false;
  var pending = null;

  function storedPairs() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    } catch (e) {
      return {};
    }
  }

  // A multi-item /cart/add.js is a bundle: the first line is the host and any
  // follow-up line carrying a selling plan is its subscription gift. Recording
  // the pair here covers every surface that adds one (hp-vrh on the homepage,
  // the PDP buy box) without each section having to do it itself.
  function rememberPairs(body) {
    if (typeof body !== 'string') return;
    var items;
    try {
      items = JSON.parse(body).items;
    } catch (e) {
      return;
    }
    if (!items || items.length < 2 || !items[0] || !items[0].id) return;

    var host = String(items[0].id);
    var pairs = storedPairs();
    var changed = false;
    items.slice(1).forEach(function (item) {
      if (!item || !item.id || !item.selling_plan) return;
      pairs[String(item.id)] = host;
      changed = true;
    });
    if (!changed) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pairs));
    } catch (e) {}
  }

  function forgetPairs(giftVariantIds) {
    if (!giftVariantIds.length) return;
    try {
      var pairs = storedPairs();
      giftVariantIds.forEach(function (id) { delete pairs[id]; });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pairs));
    } catch (e) {}
  }

  function changeQuantityToZero(id) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: String(id), quantity: 0 }),
    }).then(function (r) { return r.ok; });
  }

  // Resolves to whether the line is actually gone. Rebuy rebuilds the line, so
  // the key from the /cart.js snapshot can already be stale by now; the variant
  // id addresses the line regardless of that churn.
  function removeLine(item) {
    return changeQuantityToZero(item.key)
      .then(function (ok) { return ok || changeQuantityToZero(item.variant_id); })
      .catch(function () { return false; });
  }

  // gift variant id -> host variant id
  function collectPairs(cart) {
    var pairs = {};
    var stored = storedPairs();
    Object.keys(stored).forEach(function (giftId) {
      if (stored[giftId]) pairs[giftId] = String(stored[giftId]);
    });
    cart.items.forEach(function (item) {
      var host = item.properties && item.properties[GIFT_PROPERTY];
      if (host) pairs[String(item.variant_id)] = String(host);
    });
    return pairs;
  }

  function sync() {
    if (syncing) {
      dirty = true;
      return;
    }
    syncing = true;
    dirty = false;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var pairs = collectPairs(cart);
        var presentVariantIds = cart.items.map(function (item) { return String(item.variant_id); });

        var orphanedLines = cart.items.filter(function (item) {
          var variantId = String(item.variant_id);

          var host = pairs[variantId];
          if (host && presentVariantIds.indexOf(host) === -1) return true;

          var supersedingIds = SUPERSEDED_BY[variantId] || [];
          return supersedingIds.some(function (id) {
            return presentVariantIds.indexOf(String(id)) !== -1;
          });
        });

        // Neither side is in the cart any more (checked out, cart emptied) —
        // drop the pair so it cannot act on a later, unrelated cart.
        var stalePairs = Object.keys(pairs).filter(function (giftId) {
          return presentVariantIds.indexOf(giftId) === -1 &&
            presentVariantIds.indexOf(pairs[giftId]) === -1;
        });

        if (!orphanedLines.length) {
          forgetPairs(stalePairs);
          return false;
        }

        return Promise.all(orphanedLines.map(removeLine)).then(function (results) {
          // Keep the pair when removal failed, so the next pass can retry it
          // with a fresh line key instead of losing track of the gift.
          var removed = orphanedLines.filter(function (item, i) { return results[i]; });
          forgetPairs(stalePairs.concat(removed.map(function (item) {
            return String(item.variant_id);
          })));
          return removed.length > 0;
        });
      })
      .then(function (removed) {
        if (removed) {
          document.dispatchEvent(new CustomEvent('rebuy:cart.refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-sync' });
          }
        }
      })
      .catch(function () {})
      .then(function () {
        syncing = false;
        if (dirty) schedule();
      });
  }

  function schedule() {
    clearTimeout(pending);
    pending = setTimeout(sync, 150);
  }

  [
    'rebuy:cart.change', 'rebuy:cart.refresh', 'rebuy:cart.ready',
    'rebuy:smartcart.show', 'rebuy:smartcart.open',
    'cart:refresh', 'cart:build', 'cart-updated', 'cart:updated', 'l:cart:updated',
  ].forEach(function (evt) {
    document.addEventListener(evt, schedule);
    document.documentElement.addEventListener(evt, schedule);
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === 'gift-sync') return;
      schedule();
    });
  }

  var CART_MUTATION_URL = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var CART_ADD_URL = /\/cart\/add(\.js)?(\?|$)/;
  var originalFetch = window.fetch;
  window.fetch = function () {
    var url = arguments[0];
    var urlStr = typeof url === 'string' ? url : (url && (url.url || url.href)) || '';
    var isCartMutation = CART_MUTATION_URL.test(urlStr);
    var addBody = CART_ADD_URL.test(urlStr) && arguments[1] && arguments[1].body;
    var promise = originalFetch.apply(this, arguments);
    if (isCartMutation) {
      promise = promise.then(function (response) {
        if (response.ok) {
          if (addBody) rememberPairs(addBody);
          schedule();
        }
        return response;
      });
    }
    return promise;
  };

  // Rebuy's SmartCart mutates the cart from a worker context, so none of the
  // hooks above observe it. The poll is what actually catches that case.
  setInterval(function () {
    if (document.hidden) return;
    schedule();
  }, 3000);

  schedule();
})();
